import { cn } from '@amore/ui/cn'
import { Button } from '@amore/ui/components/button'
import { supplierCoverImage } from '@amore/ui/supplier-covers'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { Plus, SearchX } from 'lucide-react'
import { useState } from 'react'
import { newId } from '@/lib/format'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { CatalogService, Supplier } from '@/mock/types'
import { EmptyState } from '@/patterns/empty-state'
import { useToast } from '@/shell/toaster'
import { AddSupplierSheet } from './add-sheet'
import { CatalogFilters } from './catalog-filters'
import { CatalogSheet } from './catalog-sheet'
import { normalize } from './lookup'
import { categoryLabel, categoryOrder } from './model'
import { SupplierStatus } from './supplier-status'

/** Suppliers serving the whole country match every region. */
const NATIONWIDE = 'Celá ČR'

/**
 * Katalog: the verified suppliers as a grid of cards with a photo, filtered by
 * category and region chips and a search. Suppliers already in Moji
 * dodavatelé carry their status; the rest can be added straight from the card.
 */
export function CatalogContent() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const search = useSearch({ strict: false }) as { category?: string; region?: string; q?: string }
  const { catalogId } = useParams({ strict: false }) as { catalogId?: string }
  const [adding, setAdding] = useState(false)
  const catalog = data.catalog

  const categories = categoryOrder.filter((value) =>
    catalog.some((item) => item.category === value),
  )
  const regions = [...new Set(catalog.map((item) => item.region))]
    .filter((region) => region !== NATIONWIDE)
    .sort((a, b) => a.localeCompare(b, 'cs'))
  const category = categories.includes(search.category ?? '') ? (search.category as string) : 'all'
  const region = regions.includes(search.region ?? '') ? (search.region as string) : 'all'
  const query = search.q ?? ''

  const setSearch = (next: { category?: string; region?: string; q?: string }) => {
    const merged = { category, region, q: query, ...next }
    void navigate({
      to: '/suppliers/catalog',
      search: {
        ...(merged.category !== 'all' ? { category: merged.category } : {}),
        ...(merged.region !== 'all' ? { region: merged.region } : {}),
        ...(merged.q ? { q: merged.q } : {}),
      },
      replace: true,
    })
  }

  const needle = normalize(query)
  const visible = catalog.filter(
    (item) =>
      (category === 'all' || item.category === category) &&
      (region === 'all' || item.region === region || item.region === NATIONWIDE) &&
      (!needle || normalize(`${item.name} ${item.description}`).includes(needle)),
  )
  const mine = (item: CatalogService) =>
    data.suppliers.find((supplier) => supplier.catalogServiceId === item.id)

  const add = (item: CatalogService) => {
    const supplier: Supplier = {
      id: newId('sup'),
      name: item.name,
      category: item.category,
      status: 'inquiry',
      statusChangedAt: '2026-10-19',
      contacts: [],
      notes: '',
      catalogServiceId: item.id,
    }
    update((current) => ({ ...current, suppliers: [...current.suppliers, supplier] }))
    showToast({
      title: 'Dodavatel přidán',
      description: `${item.name} je mezi vašimi dodavateli`,
      tone: 'success',
      action: {
        label: 'Otevřít',
        altText: 'Otevřít přidaného dodavatele',
        onClick: () => void navigate({ to: `/suppliers/${supplier.id}` }),
      },
    })
  }
  const listSearch = {
    ...(category !== 'all' ? { category } : {}),
    ...(region !== 'all' ? { region } : {}),
    ...(query ? { q: query } : {}),
  }
  const open = (item: CatalogService) =>
    void navigate({ to: `/suppliers/catalog/${item.id}`, search: listSearch })
  const opened = catalog.find((item) => item.id === catalogId)

  return (
    <>
      <CatalogFilters
        categories={categories}
        regions={regions}
        category={category}
        region={region}
        query={query}
        count={visible.length}
        onChange={setSearch}
      />

      {visible.length === 0 ? (
        <EmptyState
          icon={<SearchX aria-hidden="true" />}
          title="Nic takového v katalogu nemáme"
          description="Zkuste jinou kategorii nebo kraj. Svého dodavatele si můžete přidat i sami."
          actions={
            <>
              <Button
                appearance="outline"
                tone="neutral"
                onClick={() => setSearch({ category: 'all', region: 'all', q: '' })}
              >
                Zrušit filtry
              </Button>
              {canEdit ? (
                <Button onClick={() => setAdding(true)}>
                  <Plus aria-hidden="true" className="size-4" /> Přidat vlastního
                </Button>
              ) : null}
            </>
          }
        />
      ) : (
        <ul className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((item) => {
            const existing = mine(item)
            return (
              <li key={item.id}>
                <CatalogCard
                  item={item}
                  existing={existing}
                  canAdd={canEdit && !existing}
                  onOpen={() => open(item)}
                  onAdd={() => add(item)}
                />
              </li>
            )
          })}
        </ul>
      )}

      {opened ? (
        <CatalogSheet
          key={opened.id}
          item={opened}
          existing={mine(opened)}
          canAdd={canEdit}
          onAdd={() => add(opened)}
          onOpenMine={(id) => void navigate({ to: `/suppliers/${id}` })}
          onClose={() => void navigate({ to: '/suppliers/catalog', search: listSearch })}
        />
      ) : null}
      {adding ? <AddSupplierSheet onClose={() => setAdding(false)} /> : null}
    </>
  )
}

function CatalogCard({
  item,
  existing,
  canAdd,
  onOpen,
  onAdd,
}: {
  item: CatalogService
  existing?: Supplier
  canAdd: boolean
  onOpen: () => void
  onAdd: () => void
}) {
  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-panel border border-border bg-card transition-shadow hover:shadow-md">
      <div className="relative">
        <img
          src={supplierCoverImage(item.category)}
          alt=""
          className="aspect-[4/3] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
        />
        {existing ? (
          <span className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-card/95 py-1 pr-1 pl-3 text-xs font-semibold shadow-sm">
            U vás
            <SupplierStatus status={existing.status} />
          </span>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-5">
        <h3 className="font-heading text-xl leading-tight">
          {/* The whole card opens the detail; the add button sits above this link. */}
          <button
            type="button"
            onClick={onOpen}
            className="text-left after:absolute after:inset-0 focus-visible:outline-none"
          >
            {item.name}
          </button>
        </h3>
        <p className="text-xs text-muted-foreground">
          {categoryLabel(item.category)} · {item.region}
        </p>
        <p className="line-clamp-2 text-sm text-foreground/80">{item.description}</p>
        {canAdd ? (
          <div className="mt-auto pt-3">
            <Button
              appearance="outline"
              tone="neutral"
              size="sm"
              onClick={onAdd}
              className={cn('relative z-10')}
            >
              <Plus aria-hidden="true" className="size-3.5" /> Přidat mezi moje
            </Button>
          </div>
        ) : null}
      </div>
    </article>
  )
}
